// Reusable section components used across pages.
import { Star } from "lucide-react";

export function StatCard({ label, value, hint, icon: Icon, accent = "#6366F1", testid }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-md p-5" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-zinc-500 font-medium">{label}</div>
          <div className="mt-2 text-2xl lg:text-3xl font-bold font-outfit text-zinc-900">{value}</div>
          {hint && <div className="text-xs text-zinc-500 mt-1">{hint}</div>}
        </div>
        {Icon && (
          <div className="w-9 h-9 rounded-md grid place-items-center" style={{ backgroundColor: `${accent}15`, color: accent }}>
            <Icon className="w-4 h-4" />
          </div>
        )}
      </div>
    </div>
  );
}

export function SectionCard({ title, action, children, testid }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-md" data-testid={testid}>
      <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200">
        <h3 className="text-base font-semibold font-outfit">{title}</h3>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold font-outfit tracking-tight text-zinc-900">{title}</h1>
        {subtitle && <p className="text-sm text-zinc-500 mt-1">{subtitle}</p>}
      </div>
      {actions && <div className="flex gap-2">{actions}</div>}
    </div>
  );
}

export function Stars({ value = 0, size = 14 }) {
  const v = Math.round(value);
  return (
    <div className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={i <= v ? "fill-amber-400 text-amber-400" : "text-zinc-300"} style={{ width: size, height: size }} />
      ))}
    </div>
  );
}

export function StatusBadge({ status }) {
  const map = {
    Verified: "bg-emerald-50 text-emerald-700 border-emerald-200",
    Valid: "bg-emerald-50 text-emerald-700 border-emerald-200",
    Active: "bg-emerald-50 text-emerald-700 border-emerald-200",
    Accepted: "bg-emerald-50 text-emerald-700 border-emerald-200",
    Pending: "bg-amber-50 text-amber-700 border-amber-200",
    "Expiring Soon": "bg-amber-50 text-amber-700 border-amber-200",
    Sent: "bg-indigo-50 text-indigo-700 border-indigo-200",
    "Responses Received": "bg-indigo-50 text-indigo-700 border-indigo-200",
    Draft: "bg-zinc-100 text-zinc-700 border-zinc-200",
    Closed: "bg-zinc-100 text-zinc-700 border-zinc-200",
    Expired: "bg-red-50 text-red-700 border-red-200",
    Rejected: "bg-red-50 text-red-700 border-red-200",
    Unverified: "bg-red-50 text-red-700 border-red-200",
    New: "bg-indigo-50 text-indigo-700 border-indigo-200",
    Responded: "bg-emerald-50 text-emerald-700 border-emerald-200",
    Submitted: "bg-indigo-50 text-indigo-700 border-indigo-200",
    Paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
    Approved: "bg-indigo-50 text-indigo-700 border-indigo-200",
  };
  const cls = map[status] || "bg-zinc-100 text-zinc-700 border-zinc-200";
  return <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border ${cls}`}>{status}</span>;
}

export function Empty({ title, subtitle, action }) {
  return (
    <div className="text-center py-12 px-6 border-2 border-dashed border-zinc-200 rounded-md">
      <div className="text-base font-semibold text-zinc-700">{title}</div>
      {subtitle && <div className="text-sm text-zinc-500 mt-1">{subtitle}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}
